// CompositionWorkbench<D extends BaseDraft> — the generic composer-draft
// workbench (ADR-0010). One shape, every composer: brief → source
// inventory → composition plan → counterfactual → agent invariants →
// variant tabs with inline citations → agent sections → handoff chain →
// redline → glass-box trace → decision bar.
//
// The workbench takes the reviewable KIND and reads the rest: the header
// eyebrow from `REVIEWABLE_KIND_SPECS` (`@septima/contract`, the one
// declaration of a reviewable kind) and the approve / reject /
// request-revision wiring from `compositionActionsByKind`.
//
// Kind-specific content is injected via render slots — never branched on
// the kind inside this file:
//   - `renderInvariants` — the agent's invariant block (SCRIVENER
//     recommendations / risk warnings / fees).
//   - `renderSections` — agent-specific sections (HERALD pack documents,
//     FORUM RAG matrix, ARBITER verdict pill).

import { useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui';
import { useToast } from '@/lib/useToast';
import { REVIEWABLE_KIND_SPECS, type WorkbenchKind } from '@septima/contract';
import type { BaseDraft } from '@/data/types';
import RejectionDialog from '../RejectionDialog';
import BriefBlock from './brief-block';
import SourceInventoryBlock from './source-inventory-block';
import CompositionPlanBlock from './composition-plan-block';
import CounterfactualBlock from './counterfactual-block';
import VariantTabsWithCitations from './variant-tabs-with-citations';
import HandoffChainBlock from './handoff-chain-block';
import RedlineBlock from './redline-block';
import DecisionBar from './decision-bar';
import WorkbenchGlassBox from './workbench-glass-box';
import { compositionActionsByKind, type CompositionActions } from './composition-agent-config';

export interface CompositionWorkbenchProps<D extends BaseDraft> {
    /** The reviewable kind. It gives the header eyebrow and the wiring. */
    kind: WorkbenchKind;
    draft: D;
    /** Hide the decision bar — read-only contexts. */
    readOnly?: boolean;
    /** Agent-specific invariants block (held across variants). */
    renderInvariants?: (draft: D) => ReactNode;
    /** Agent-specific section list. */
    renderSections?: (draft: D) => ReactNode;
    /** Post-success lifecycle hooks. */
    onApproved?: (draft: D) => void;
    onRejected?: (draft: D) => void;
    onRevisionRequested?: (draft: D) => void;
}

export default function CompositionWorkbench<D extends BaseDraft>({
    kind,
    draft,
    readOnly = false,
    renderInvariants,
    renderSections,
    onApproved,
    onRejected,
    onRevisionRequested,
}: CompositionWorkbenchProps<D>) {
    const agentName = REVIEWABLE_KIND_SPECS[kind].agentLabel;
    const actions: CompositionActions = compositionActionsByKind[kind];
    const { pushToast } = useToast();
    const queryClient = useQueryClient();
    const [busy, setBusy] = useState(false);
    const [dialogMode, setDialogMode] = useState<'reject' | 'revision' | null>(null);

    const isPending = draft.reviewStatus === 'pending';

    async function runAction(spec: typeof actions.approve, reason?: string): Promise<boolean> {
        if (busy) return false;
        setBusy(true);
        try {
            const result = await spec.dispatch(draft.id, reason);
            if (result.ok) {
                pushToast({ kind: 'success', message: spec.successToast });
                // The Needs-Review column reads the review-queue aggregate +
                // per-table agent-rows cache — nudge both so a decided draft
                // drops out immediately instead of waiting on their 10s/30s polls.
                void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
                void queryClient.invalidateQueries({ queryKey: ['agent', 'rows'] });
                return true;
            }
            pushToast({ kind: 'error', message: result.error ?? 'Action failed' });
            return false;
        } catch (err) {
            pushToast({ kind: 'error', message: err instanceof Error ? err.message : 'Failed' });
            return false;
        } finally {
            setBusy(false);
        }
    }

    async function handleApprove(): Promise<void> {
        const ok = await runAction(actions.approve);
        if (ok) onApproved?.(draft);
    }

    async function handleReject(reason: string): Promise<void> {
        const ok = await runAction(actions.reject, reason || 'Rejected by reviewer');
        if (ok) onRejected?.(draft);
    }

    async function handleRevision(reason: string): Promise<void> {
        if (!actions.requestRevision) return;
        const ok = await runAction(
            actions.requestRevision,
            reason || 'Revision requested by reviewer',
        );
        if (ok) onRevisionRequested?.(draft);
    }

    const dialogConfig =
        dialogMode === 'revision' && actions.requestRevision
            ? {
                  title: `Request revision · ${agentName}`,
                  confirmLabel: actions.requestRevision.label,
                  placeholder:
                      'Tell the agent what to change so it can re-roll without the same gap.',
              }
            : {
                  title: `Reject ${agentName} draft`,
                  confirmLabel: actions.reject.label,
                  placeholder:
                      'Optional — note why so the agent can re-roll without the same mistake.',
              };

    return (
        <Card noPadding className="border border-[rgb(var(--color-divider))]">
            <div className="px-card-padding pt-card-padding pb-2 border-b border-[rgb(var(--color-divider))]">
                <span
                    data-testid="workbench-agent-name"
                    className="text-card-title text-[rgb(var(--color-text-primary))]"
                >
                    {agentName} · composition workbench
                </span>
            </div>

            <div
                data-testid="composition-workbench"
                className="px-card-padding py-card-padding flex flex-col gap-3"
            >
                <BriefBlock brief={draft.brief} />
                <SourceInventoryBlock inventory={draft.sourceInventory} />
                <CompositionPlanBlock plan={draft.compositionPlan} />

                {draft.counterfactualAnchor && (
                    <CounterfactualBlock
                        anchor={draft.counterfactualAnchor}
                        label="What would change this draft"
                    />
                )}

                {renderInvariants && <div>{renderInvariants(draft)}</div>}

                <VariantTabsWithCitations
                    variants={draft.variants}
                    citations={draft.citations}
                    selectedVariantIndex={draft.selectedVariantIndex}
                />

                {renderSections && <div>{renderSections(draft)}</div>}

                <HandoffChainBlock chain={draft.handoffChain} />

                {draft.redlineVsPrior && <RedlineBlock redline={draft.redlineVsPrior} />}

                <WorkbenchGlassBox draft={draft} />

                {draft.reviewStatus === 'rejected' && draft.rejectionReason && (
                    <div className="rounded-card bg-[rgb(var(--color-rag-red)/0.08)] px-3 py-2">
                        <div className="text-annotation font-medium text-[rgb(var(--color-rag-red))]">
                            Rejection reason
                        </div>
                        <p className="text-annotation text-[rgb(var(--color-text-secondary))]">
                            {draft.rejectionReason}
                        </p>
                    </div>
                )}

                {isPending && !readOnly && (
                    <DecisionBar
                        actions={actions}
                        busy={busy}
                        onApprove={() => void handleApprove()}
                        onReject={() => setDialogMode('reject')}
                        onRequestRevision={() => setDialogMode('revision')}
                    />
                )}

                {draft.materialisedLetterId && (
                    <div className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                        Materialised as{' '}
                        <span className="font-mono">{draft.materialisedLetterId}</span>
                    </div>
                )}
            </div>

            <RejectionDialog
                open={dialogMode !== null}
                title={dialogConfig.title}
                placeholder={dialogConfig.placeholder}
                confirmLabel={dialogConfig.confirmLabel}
                onClose={() => setDialogMode(null)}
                onConfirm={(reason) => {
                    const mode = dialogMode;
                    setDialogMode(null);
                    if (mode === 'revision') void handleRevision(reason);
                    else void handleReject(reason);
                }}
            />
        </Card>
    );
}
